/**
 * Silences ONE specific third-party error: reCAPTCHA's teardown race.
 *
 * WHAT: after phone sign-in succeeds, React unmounts the sign-in screen. Google's
 * recaptcha__en.js still has queued callbacks and token-expiry timers holding
 * references to the container it rendered into, so one of them fires against a
 * node React has already removed and throws:
 *
 *     TypeError: Cannot read properties of null (reading 'style')
 *         at ... /recaptcha/releases/<build>/recaptcha__en.js
 *
 * WHY SUPPRESS RATHER THAN FIX: the throw happens inside Google's minified
 * script, on work it scheduled itself. We already do everything available to us
 * — the container is a single node that is never swapped between steps, and the
 * verifier is cleared immediately after a successful send, while its container
 * is still attached. The remaining race is not ours to win.
 *
 * WHY IT IS SAFE: it fires strictly AFTER sign-in has succeeded and changes no
 * application state. Its only real effect is CRA's dev error overlay covering
 * the screen; in a production build it would be a console entry.
 *
 * THIS IS DELIBERATELY NARROW. The filter requires BOTH the exact message and a
 * stack originating in recaptcha__en.js. Any other null-property error, and
 * every error from our own code, propagates untouched. If you ever widen this,
 * you will start hiding real bugs.
 */

const isRecaptchaTeardownError = (message, source, error) => {
  const msg = String(message || error?.message || '');
  const stack = String(error?.stack || '');
  const src = String(source || '');

  const matchesMessage =
    /Cannot read propert(?:y|ies) of null \(reading .style.\)/.test(msg) ||
    /null is not an object.*\.style/.test(msg); // Safari's wording

  const fromRecaptcha = /recaptcha__?[a-z]{2}\.js|\/recaptcha\/releases\//.test(`${src}${stack}`);

  return matchesMessage && fromRecaptcha;
};

export default function suppressRecaptchaTeardownError() {
  // Capture phase, registered before CRA's overlay attaches its own listener,
  // so stopping propagation here keeps the overlay from rendering.
  window.addEventListener(
    'error',
    (event) => {
      if (isRecaptchaTeardownError(event.message, event.filename, event.error)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // eslint-disable-next-line no-console
        console.debug('[recaptcha] suppressed known teardown error (harmless, post-sign-in)');
        return false;
      }
      return undefined;
    },
    true
  );

  // The same failure can surface as a rejected promise.
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event.reason;
      if (isRecaptchaTeardownError(reason?.message, '', reason)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // eslint-disable-next-line no-console
        console.debug('[recaptcha] suppressed known teardown rejection');
      }
    },
    true
  );
}
